const NOTES = [
  { frequency: 523.25, duration: 0.3, attack: 0.02, release: 0.08 }, // C5
  { frequency: 659.25, duration: 0.3, attack: 0.02, release: 0.08 }, // E5
  { frequency: 783.99, duration: 0.3, attack: 0.02, release: 0.08 }, // G5
  { frequency: 659.25, duration: 0.3, attack: 0.02, release: 0.08 }, // E5
  { frequency: 523.25, duration: 0.6, attack: 0.02, release: 0.35 }, // back to C5, fading out
];
const GAIN = 0.25;
const LEAD_IN_SECONDS = 0.15;
// Safety net for a context that stops advancing without reporting an error: give up this long after
// the figure should have ended and discard the context.
const STALL_GRACE_SECONDS = 1;

type SinkAware = AudioContext & { setSinkId: (sinkId: string) => Promise<void> };
type SinkAwareOptions = AudioContextOptions & { sinkId?: string };

export type TestToneOutput = {
  play: (sinkId: string) => Promise<void>;
  close: () => Promise<void>;
};

/**
 * The context stays open between plays because restarting it swallows the beginning of the first
 * note. When the speaker goes away, Chromium reports an `error` and may fall back to the default
 * device; the tone must not continue on a speaker the user did not pick, so the context is discarded
 * and a pending play fails.
 */
export function createTestToneOutput(): TestToneOutput {
  let context: SinkAware | null = null;
  let sinkId: string | null = null;
  let settlePlaying: ((failure?: Error) => void) | null = null;
  let stallTimer: ReturnType<typeof setTimeout> | null = null;
  let closeCount = 0;

  const discard = (target: SinkAware) => {
    if (context === target) {
      context = null;
      sinkId = null;
    }
    void target.close().catch(() => undefined);
  };

  const contextFor = async (nextSinkId: string): Promise<SinkAware> => {
    if (!context || context.state === 'closed') {
      const options: SinkAwareOptions = { sinkId: nextSinkId };
      const created = new AudioContext(options) as SinkAware;
      created.addEventListener('error', () => {
        discard(created);
        settlePlaying?.(new Error('The audio output device reported an error'));
      });
      context = created;
      sinkId = nextSinkId;
    }
    const target = context;
    if (sinkId !== nextSinkId) {
      // A rejected change leaves the context on its previous sink, so the remembered one stays valid.
      await target.setSinkId(nextSinkId);
      sinkId = nextSinkId;
    }
    if (target.state !== 'running') await target.resume();
    return target;
  };

  return {
    async play(nextSinkId) {
      const closesBefore = closeCount;
      let target: SinkAware;
      try {
        target = await contextFor(nextSinkId);
      } catch (error) {
        // Closed while we were getting ready: nothing left to play on, and nothing to report.
        if (closeCount !== closesBefore) return;
        throw error;
      }
      if (target.state === 'closed') return;
      let start = target.currentTime + LEAD_IN_SECONDS;
      let last: OscillatorNode | undefined;
      for (const note of NOTES) {
        const end = start + note.duration;
        const envelope = target.createGain();
        envelope.gain.setValueAtTime(0, start);
        envelope.gain.linearRampToValueAtTime(GAIN, start + note.attack);
        envelope.gain.setValueAtTime(GAIN, end - note.release);
        envelope.gain.linearRampToValueAtTime(0, end);
        envelope.connect(target.destination);

        const oscillator = target.createOscillator();
        oscillator.type = 'triangle';
        oscillator.frequency.value = note.frequency;
        oscillator.connect(envelope);
        oscillator.start(start);
        oscillator.stop(end);
        start = end;
        last = oscillator;
      }
      if (!last) return;
      const remainingSeconds = start - target.currentTime + STALL_GRACE_SECONDS;
      let ended = false;
      try {
        await new Promise<void>((resolve, reject) => {
          settlePlaying = (failure) => (failure ? reject(failure) : resolve());
          last.onended = () => {
            ended = true;
            resolve();
          };
          stallTimer = setTimeout(resolve, remainingSeconds * 1000);
        });
      } finally {
        if (stallTimer) clearTimeout(stallTimer);
        stallTimer = null;
        settlePlaying = null;
      }
      if (!ended) discard(target);
    },
    async close() {
      closeCount += 1;
      // Closing mid-play must not leave the caller waiting for an `ended` that never comes.
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = null;
      settlePlaying?.();
      settlePlaying = null;
      const closing = context;
      context = null;
      sinkId = null;
      if (closing && closing.state !== 'closed') await closing.close();
    },
  };
}
